import type { CurrentUser } from '@zhiyue/core';
import {
  getOpenPlatformToken,
  type OpenPlatformTokenResult,
} from '@/services/bff/open-platform.api';

export type { OpenPlatformTokenResult } from '@/services/bff/open-platform.api';

const STORAGE_KEY = 'contract.openPlatformAccessToken';
const REFRESH_BUFFER_MS = 60_000;

type TokenUser = Pick<CurrentUser, 'memberUuid'> & {
  organizationUuid?: string;
};
type CachedToken = OpenPlatformTokenResult & { cacheKey: string };

let pendingRequest:
  | { cacheKey: string; promise: Promise<OpenPlatformTokenResult> }
  | undefined;

const normalizeText = (value: unknown) => String(value ?? '').trim();
const getCacheKey = (user: TokenUser) =>
  [normalizeText(user.organizationUuid), normalizeText(user.memberUuid)].join(
    '|',
  );

const readCachedToken = (cacheKey: string): OpenPlatformTokenResult | null => {
  try {
    const rawValue = sessionStorage.getItem(STORAGE_KEY);
    if (!rawValue) return null;

    const value = JSON.parse(rawValue) as CachedToken;
    if (
      value.cacheKey !== cacheKey ||
      typeof value.accessToken !== 'string' ||
      !value.accessToken.trim() ||
      typeof value.expiresAt !== 'number' ||
      value.expiresAt <= Date.now() + REFRESH_BUFFER_MS
    ) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return { accessToken: value.accessToken, expiresAt: value.expiresAt };
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const clearOpenPlatformToken = () => {
  pendingRequest = undefined;
  sessionStorage.removeItem(STORAGE_KEY);
};

export const getCachedOpenPlatformToken = (user: TokenUser) =>
  readCachedToken(getCacheKey(user));

export const fetchOpenPlatformToken = async (
  user: TokenUser,
): Promise<OpenPlatformTokenResult> => {
  const cacheKey = getCacheKey(user);
  if (
    !normalizeText(user.organizationUuid) ||
    !normalizeText(user.memberUuid)
  ) {
    clearOpenPlatformToken();
    throw new Error('当前会话缺少开放平台认证信息');
  }

  const cachedToken = readCachedToken(cacheKey);
  if (cachedToken) return cachedToken;
  if (pendingRequest?.cacheKey === cacheKey) return pendingRequest.promise;

  const promise = getOpenPlatformToken().then((result) => {
    if (
      typeof result?.accessToken !== 'string' ||
      !result.accessToken.trim() ||
      typeof result.expiresAt !== 'number'
    ) {
      throw new Error('开放平台 Token 响应无效');
    }
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...result, cacheKey }),
    );
    return result;
  });
  pendingRequest = { cacheKey, promise };

  try {
    return await promise;
  } finally {
    if (pendingRequest?.promise === promise) pendingRequest = undefined;
  }
};
